import { useMutation, useQueryClient } from 'react-query';
import isArray from 'lodash/isArray';

import { DeleteItemGqlQuery, DeleteItemCacheKeys } from '../../../../../types';

import { deleteCacheItem } from '../../actions/deleteCacheItem';

import {
  ErrorType,
  parseRequestError
} from '../../../../../utils/parseRequestError';

interface CacheDeleteQueryOptions<DeleteQueryInput> {
  action: string;
  query: DeleteItemGqlQuery;
  cacheKeys?: DeleteItemCacheKeys;
  onOptimisticDelete?: (input: DeleteQueryInput) => null | (() => void);
  refetchInactive?: boolean;
}

interface CacheDeleteQueryContext {
  rollback?: () => void;
}

function useCacheDeleteQuery<
  DeleteQueryInput,
  DeleteQueryResponse,
  DeleteQueryError extends ErrorType
>({
  action,
  query,
  cacheKeys,
  onOptimisticDelete,
  refetchInactive = false
}: CacheDeleteQueryOptions<DeleteQueryInput>) {
  const queryClient = useQueryClient();

  const { data, error, isLoading, mutateAsync, reset } = useMutation<
    DeleteQueryResponse,
    DeleteQueryError,
    DeleteQueryInput,
    CacheDeleteQueryContext
  >(
    (queryInput) =>
      deleteCacheItem<DeleteQueryInput, DeleteQueryResponse>({
        action,
        query,
        queryInput
      }),
    {
      onMutate: (input) => {
        const rollback = onOptimisticDelete?.(input) || undefined;

        return { rollback };
      },
      onError: (error, variables, context) => {
        context?.rollback?.();
      },
      onSettled: async () => {
        if (isArray(cacheKeys)) {
          cacheKeys.map((eachCacheKey) =>
            queryClient.invalidateQueries(eachCacheKey, { refetchInactive })
          );
        }
      }
    }
  );

  return {
    deleteCacheQueryData: data,
    deleteCacheQueryError: error,
    deleteCacheQueryLoading: isLoading,
    deleteCacheQueryErrorMessage: parseRequestError(error),
    deleteCacheQuery: mutateAsync,
    deleteCacheQueryReset: reset
  };
}

export default useCacheDeleteQuery;
